import React from "react";
import { CircleCheck, Hammer, Clock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurRemodelingStory() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const coreValues = [{
    label: "SYSTEMS",
    title: "Infrastructure",
    icon: <CircleCheck className="w-5 h-5 text-primary" />,
    desc: "AI nodes built for 24/7 operational resilience."
  }, {
    label: "LEGACY",
    title: "Authority",
    icon: <ShieldCheck className="w-5 h-5 text-primary" />,
    desc: "Honoring 20th-century grit with 21st-century code."
  }, {
    label: "RECOVERY",
    title: "Precision",
    icon: <Clock className="w-5 h-5 text-primary" />,
    desc: "Zero missed calls. Zero wasted revenue."
  }];
  return <section data-section-id="20313" id="the-blueprint-of-excellence" ref={sectionRef} className="relative w-full py-24 overflow-hidden dark bg-background text-foreground">
      <style>{`
        body {
          background-color: var(--background);
          color: var(--foreground);
        }
      `}</style>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-none blur-3xl animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[600px] h-[600px] bg-secondary/10 rounded-none blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-[40%] left-[20%] w-[300px] h-[300px] bg-primary/5 rounded-none blur-3xl opacity-50" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 z-10 space-y-32">
        {}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="relative group flex justify-center lg:justify-end">
            <div className="relative w-72 h-72 sm:w-96 sm:h-96 lg:w-[450px] lg:h-[450px]">
              <div className="absolute inset-0 rounded-none bg-primary/10 blur-2xl scale-95 group-hover:scale-105 transition-transform duration-700" />
              <img src="https://neutral-cowbird.10web.cloud/wp-content/uploads/2026/03/remodeling_story_craftsmanship.webp" alt="Honoring 20th-century plumbing craftsmanship with 21st-century engineering" className="relative w-full h-full object-cover rounded-none shadow-[0_0_40px_rgba(181,166,66,0.2)] border-2 border-primary/20 z-10 transition-transform duration-700 group-hover:-translate-y-2" />
              {}
              <div className="absolute -bottom-6 -right-6 bg-card p-4 rounded-none shadow-xl border border-border hidden sm:block">
                <Hammer className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6 text-center lg:text-left">
            <h2 className="text-3xl md:text-4xl lg:text-6xl font-serif font-bold text-primary tracking-tighter uppercase">
              The Blueprint of Excellence
            </h2>
            <div className="space-y-4">
              <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
                Honoring 20th-century plumbing craftsmanship with 21st-century engineering. At Exergy Ops, we treat your business infrastructure like our mother's house: with absolute respect and zero shortcuts.
              </p>
              <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
                Exergy Ops is the invisible infrastructure that captures every missed lead and injects it into your CRM. We transform call takers into system owners through industrial-grade AI and mechanical precision.
              </p>
            </div>
            <div className="pt-4">
              <Button asChild size="lg" className="rounded-none px-10 bg-primary text-primary-foreground hover:bg-primary/90 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] transition-all">
                <Link to="/TheEnginePage">DEPLOY THE ENGINE</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="order-2 lg:order-1 space-y-10">
            <div className="space-y-6 text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight uppercase">
                Engineered for the Elite
              </h2>
              <p className="text-lg text-muted-foreground font-sans leading-relaxed">
                Our reputation is built on three industrial pillars that guide every node we deploy. These aren't just values; they are the mechanical certainties that drive your revenue recovery.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {coreValues.map((item, i) => <Card key={i} data-index={i} className="bg-card/40 backdrop-blur-sm border-primary/20 rounded-none hover:border-primary transition-colors duration-300 shadow-sm hover:shadow-lg">
                  <CardContent className="p-5 flex flex-col items-center text-center space-y-3">
                    <div className="p-2 bg-primary/10 rounded-none">
                      {item.icon}
                    </div>
                    <span className="text-xs font-mono font-bold text-primary tracking-widest uppercase">
                      {item.label}
                    </span>
                    <h4 className="font-serif font-semibold text-foreground">
                      {item.title}
                    </h4>
                  </CardContent>
                </Card>)}
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 relative group flex justify-center lg:justify-start">
            <div className="relative w-72 h-72 sm:w-96 sm:h-96 lg:w-[450px] lg:h-[450px]">
              <div className="absolute inset-0 rounded-none bg-secondary/10 blur-2xl scale-95 group-hover:scale-105 transition-transform duration-700" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/remodeling_story_teamwork.png" alt="Team reviewing blueprints on a construction site" className="relative w-full h-full object-cover rounded-none shadow-[0_0_40px_rgba(181,166,66,0.2)] border-2 border-primary/20 z-10 transition-transform duration-700 group-hover:-translate-y-2" />
              {}
              <div className="absolute -top-6 -left-6 bg-card p-4 rounded-none shadow-xl border border-border hidden sm:block">
                <div className="text-center">
                  <span className="block text-2xl font-serif font-bold text-primary">ZERO</span>
                  <span className="text-xs text-muted-foreground font-mono uppercase">Shortcuts.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}